import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { OwlMark } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bone px-4">
      <Seo title="Owner Login — The Owl's Nest" noindex />
      <div className="w-full max-w-md">
        <div className="text-center">
          <Link to="/" aria-label="Back to site"><OwlMark className="mx-auto h-12 w-12" /></Link>
          <h1 className="mt-5 font-serif text-3xl text-ink">The back office</h1>
          <p className="mt-2 text-sm text-stone-500">Sign in to manage bookings and inquiries.</p>
        </div>
        <form onSubmit={submit} className="card mt-8 space-y-5 p-8" data-testid="admin-login-form">
          {error && (
            <p className="rounded-xl bg-red-50 p-3.5 text-sm text-red-800 ring-1 ring-red-700/15" role="alert" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <div>
            <label htmlFor="admin-email" className="label">Email</label>
            <input id="admin-email" type="email" required value={email} autoComplete="email"
              data-testid="admin-login-email" onChange={(e) => setEmail(e.target.value)} className="field" />
          </div>
          <div>
            <label htmlFor="admin-password" className="label">Password</label>
            <input id="admin-password" type="password" required value={password} autoComplete="current-password"
              data-testid="admin-login-password" onChange={(e) => setPassword(e.target.value)} className="field" />
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-button"
            className="btn-dark w-full disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </button>
          <div className="flex items-center gap-4 text-xs text-stone-400">
            <span className="h-px flex-1 bg-black/10" /> or <span className="h-px flex-1 bg-black/10" />
          </div>
          <button type="button" onClick={googleLogin} data-testid="admin-google-login-button" className="btn-ghost w-full">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.1 3.7-8.8z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-2.9 3.6-5 6.8-5z"/></svg>
            Continue with Google
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-stone-400">
          <Link to="/" className="underline underline-offset-4 hover:text-stone-600">Back to the website</Link>
        </p>
      </div>
    </div>
  );
}
